import { ChannelIdentity } from '@msdyn365-commerce/retail-proxy';

/**
 * Extension property key used to mark a channel identity that came from module configuration.
 */
export const MIL_CONFIGURED_CHANNEL_KEY = 'MilConfiguredChannel';

/**
 * Channel type value for a call center channel. The retail proxy RetailChannelType enum is
 * extensible and does not declare this member, so the value is duplicated here the same way the
 * order-management module does it.
 */
const CALL_CENTER_CHANNEL_TYPE_VALUE = 4;

/**
 * A single entry of the "Additional channels" module configuration.
 */
export interface IMilAdditionalChannel {
    channelDisplayName?: string;
    channelRecordId?: string;
}

/**
 * Builds channel identities for the channels that GetChannels does not return.
 *
 * F&O blocks call center channels from being copied to the AX channel table, so those channels
 * never come back from GetChannels. Supplying them from configuration puts them into the Origin
 * filter and lets their names resolve, without any F&O or CSU change.
 * @param additionalChannels Entries from the module's additionalChannels configuration.
 * @param existingChannels Channel identities already returned by GetChannels.
 * @returns Channel identities for the configured channels, tagged so they can be recognized later.
 */
export const BuildMilConfiguredChannelIdentities = (
    additionalChannels: IMilAdditionalChannel[] | undefined,
    existingChannels: ChannelIdentity[]
): ChannelIdentity[] => {
    if (!additionalChannels || additionalChannels.length === 0) {
        return [];
    }

    const seenRecordIds = new Set<number>(existingChannels.map(channel => channel.RecordId));
    const configuredChannels: ChannelIdentity[] = [];

    for (const additionalChannel of additionalChannels) {
        const channelRecordId = additionalChannel.channelRecordId?.trim();
        const displayName = additionalChannel.channelDisplayName?.trim();
        const recordId = Number(channelRecordId);

        // Commerce channel record ids are ten-digit positive integers. Reject malformed
        // configuration before it can appear in the Origin refiner.
        if (!channelRecordId || (!Number.isSafeInteger(recordId) && recordId > 0) || !displayName || seenRecordIds.has(recordId)) {
            continue;
        }

        seenRecordIds.add(recordId);
        configuredChannels.push({
            RecordId: recordId,
            Name: displayName,

            // Typing these as call center keeps them out of the online-store code paths, which is
            // what drives the "placed by / on behalf of" logic in the order history components.
            ChannelType: { Name: 'CallCenter', Value: CALL_CENTER_CHANNEL_TYPE_VALUE },
            ExtensionProperties: [{ Key: MIL_CONFIGURED_CHANNEL_KEY, Value: { BooleanValue: true } }]
        });
    }

    return configuredChannels;
};

export const isMilConfiguredChannel = (channel: ChannelIdentity | undefined): boolean =>
    channel?.ExtensionProperties?.some(property => property.Key === MIL_CONFIGURED_CHANNEL_KEY) ?? false;

export const getChannelName = (
    channelTypeValue: number | undefined,
    callCenterChannelNameText: string | undefined,
    channelAddress: string | undefined,
    posChannelNameText: string | undefined,
    channelName: string | undefined,
    onlineStoreChannelNameText: string | undefined,
    isConfiguredChannel: boolean = false
): string => {
    if (!channelName) {
        return '';
    }

    if (isConfiguredChannel && channelTypeValue === CALL_CENTER_CHANNEL_TYPE_VALUE) {
        return `${callCenterChannelNameText ?? ''} ${channelName ?? ''}`.trim();
    }

    if (channelAddress) {
        return `${posChannelNameText ?? ''} ${channelName}`.trim();
    }

    return `${onlineStoreChannelNameText ?? ''} ${channelName}`.trim();
};
